import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const schema = z.object({
  serviceId: z.string().uuid(),
  planKey: z.enum(["days_5", "days_30"]),
});

/** Pay Heat to feature an already-published (free) marketplace service. */
export const promoteService = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { spendFee, getBalances } = await import("@/lib/wallets.server");
    const { PROMOTION_HEAT_CENTS, promotionPlan } = await import("./promotions");

    const plan = promotionPlan(data.planKey);
    if (!plan) throw new Error("Unknown promotion plan.");
    const amountCents = plan.heat * PROMOTION_HEAT_CENTS;

    const { data: service } = await supabaseAdmin
      .from("marketplace_services")
      .select("id, seller_user_id, status, featured_until")
      .eq("id", data.serviceId)
      .maybeSingle();
    if (!service || service.seller_user_id !== context.userId) {
      throw new Error("Service not found.");
    }
    if (service.status !== "active" && service.status !== "approved") {
      throw new Error("Only a live, approved service can be featured.");
    }

    const balances = await getBalances(context.userId);
    if (balances.referralCents + balances.topupCents < amountCents) {
      throw new Error(
        `Featuring for ${plan.days} days costs ${plan.heat} Heat. Top up your wallet and try again.`,
      );
    }

    const now = Date.now();
    const current = service.featured_until ? new Date(service.featured_until).getTime() : 0;
    const startsAt = new Date(Math.max(now, current));
    const endsAt = new Date(startsAt.getTime() + plan.days * 24 * 60 * 60 * 1000);

    const { data: promo, error } = await supabaseAdmin
      .from("marketplace_service_promotions")
      .insert({
        service_id: service.id,
        seller_user_id: context.userId,
        plan_key: plan.key,
        days: plan.days,
        heat: plan.heat,
        amount_cents: amountCents,
        starts_at: startsAt.toISOString(),
        ends_at: endsAt.toISOString(),
        status: "active",
      })
      .select("id")
      .single();
    if (error || !promo) throw error ?? new Error("Could not start the promotion.");

    const result = await spendFee(
      context.userId,
      amountCents,
      "service_feature",
      "mp_service",
      service.id,
    );
    if (result.remaining > 0) {
      await supabaseAdmin.from("marketplace_service_promotions").delete().eq("id", promo.id);
      throw new Error(
        `Featuring for ${plan.days} days costs ${plan.heat} Heat. Top up your wallet and try again.`,
      );
    }

    await supabaseAdmin
      .from("marketplace_services")
      .update({ featured_until: endsAt.toISOString() })
      .eq("id", service.id);

    return {
      ok: true as const,
      featuredUntil: endsAt.toISOString(),
      chargedCents: result.spent,
      heat: plan.heat,
      days: plan.days,
    };
  });
